"use client";

import React, { useRef } from "react";
import Image from "next/image";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import { Icon } from "@iconify/react";

type Props = {};

interface ReviewItem {
  id: number;
  name: string;
  position: string;
  img: string;
  reviewDescription: string;
}

const reviewsData: ReviewItem[] = [
  {
    id: 1,
    name: "Mark Henderson",
    position: "Everest Base Camp Trekker",
    img: "/avatar.jpg",
    reviewDescription:
      "Summitting Gokyo Ri and reaching Everest Base Camp with Prem and the Infinity Adventure team was truly life-changing. Their IFMGA safety protocols and genuine Sherpa hospitality made all the difference.",
  },
  {
    id: 2,
    name: "Sophie Laurent",
    position: "Island Peak Climber",
    img: "/avatar.jpg",
    reviewDescription:
      "The technical preparation and ice training in Chhukung gave me the confidence to stand on top of Island Peak. Flawless logistics, incredible guides, and memories I will cherish forever!",
  },
  {
    id: 3,
    name: "David & Sarah Miller",
    position: "Annapurna Circuit Explorers",
    img: "/avatar.jpg",
    reviewDescription:
      "From crossing Thorong La Pass to the scenic teahouses, everything was organized to perfection. The guides were exceptionally knowledgeable, patient, and made us feel like family throughout the journey.",
  },
  {
    id: 4,
    name: "Kenji Takahashi",
    position: "Manaslu Expedition Climber",
    img: "/avatar.jpg",
    reviewDescription:
      "High-altitude climbing requires absolute trust in your leadership team. Infinity Adventure Nepal's professional certified mountain guides deliver world-class safety, equipment, and expedition execution.",
  },
  {
    id: 5,
    name: "Elena Rostova",
    position: "Mera Peak & Wilderness Trek",
    img: "/avatar.jpg",
    reviewDescription:
      "Unbeatable experience in the Himalayas! The acclimatization pace was spot-on, the camp setup was comfortable, and our summit day on Mera Peak was one of the happiest days of my life.",
  },
];

export default function Reviews({ }: Props) {
  const sliderRef = useRef<Slider>(null);

  const handleNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const handlePrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const settings = {
    dots: true,
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    speed: 500,
    autoplaySpeed: 4500,
    arrows: false,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <section className="w-full py-16 md:py-24 bg-white relative">
      <div className="lg:w-11/12 w-11/12 3xl:w-8/12 mx-auto">
        {/* Top Centered Section Header */}
        <div className="text-center flex flex-col items-center justify-center space-y-3 max-w-3xl mx-auto mb-12">
          <h2 className="uppercase font-extrabold title lg:text-5xl md:text-4xl sm:text-3xl text-2xl text-zinc-900 text-center">
            Testimonials
          </h2>
          <div className="text-zinc-600 text-base md:text-lg leading-relaxed flex flex-col items-center">
            <span>We&apos;re proud to have helped countless adventurers create memories that last a lifetime.</span>
            <span>Read their stories of discovery, trust, and joy with Infinity Adventure.</span>
          </div>
        </div>

        {/* Testimonial Cards Carousel Below */}
        <div className="relative w-full testimonial-dots [&_.slick-dots_li_button:before]:!text-[#E25C23] [&_.slick-dots_li_button:before]:!opacity-35 [&_.slick-dots_li.slick-active_button:before]:!opacity-100 [&_.slick-dots_li.slick-active_button:before]:!text-[#E25C23] [&_.slick-dots_li_button:before]:!text-[8px] [&_.slick-dots_li.slick-active_button:before]:!text-[11px] [&_.slick-dots_li_button:before]:transition-all">
          <Slider {...settings} ref={sliderRef}>
            {reviewsData.map((item, index) => (
              <div key={index} className="pb-4 px-3">
                <div className="bg-white border border-zinc-200 rounded-md p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between gap-6 h-full min-h-[320px]">
                  {/* Top: Rating Stars & Quote Icon */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-1 text-[#E25C23]">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            icon="ic:sharp-star"
                            className="w-5 h-5"
                          />
                        ))}
                      </div>
                      <Icon
                        icon="ooui:quotes-rtl"
                        className="text-zinc-200 text-3xl"
                      />
                    </div>

                    {/* Review text */}
                    <p className="text-zinc-700 text-sm sm:text-base leading-relaxed italic">
                      &ldquo;{item.reviewDescription}&rdquo;
                    </p>
                  </div>

                  {/* Bottom: User Profile */}
                  <div className="flex items-center gap-3 pt-4 border-t border-zinc-100 mt-auto">
                    <Image
                      src={item.img}
                      width={80}
                      height={80}
                      alt={item.name}
                      className="w-12 h-12 rounded-full object-cover border border-zinc-200 shrink-0"
                    />
                    <div>
                      <h3 className="font-bold text-zinc-900 text-base">
                        {item.name}
                      </h3>
                      <p className="text-xs sm:text-sm text-zinc-500">
                        {item.position}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>

          {/* Navigation Controls Centered Below */}
          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={handlePrev}
              aria-label="Previous testimonial"
              className="cursor-pointer border border-zinc-200 rounded-md bg-white hover:bg-[#E25C23] hover:text-white hover:border-[#E25C23] text-zinc-800 p-3 transition-colors duration-200 shadow-sm"
            >
              <Icon icon="iconamoon:arrow-left-2" className="w-5 h-5" />
            </button>
            <button
              onClick={handleNext}
              aria-label="Next testimonial"
              className="cursor-pointer border border-zinc-200 rounded-md bg-white hover:bg-[#E25C23] hover:text-white hover:border-[#E25C23] text-zinc-800 p-3 transition-colors duration-200 shadow-sm"
            >
              <Icon icon="iconamoon:arrow-right-2" className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
